import { useState, useEffect, useRef } from 'react';
import { aquaApi, redirectToLogin } from '../aquaApi';
import { fmtDate } from '../dateUtils';

const S3_BASE = '/spring/aqua/api/s3';
const ROOT = 'chmr/';

// ── helpers ──────────────────────────────────────────────────────────────────

function pathSegments(path) { return path.replace(/\/$/, '').split('/').filter(Boolean); }
function fileName(path) { return path.replace(/\/$/, '').split('/').pop() || path; }
function isFolder(path) { return path.endsWith('/'); }
function parentOf(path) {
  const parts = path.replace(/\/$/, '').split('/');
  parts.pop();
  return parts.length ? parts.join('/') + '/' : ROOT;
}
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return bytes + ' B';
}
function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['jpg','jpeg','png','gif','webp','svg'].includes(ext)) return { emoji: '🖼️', color: '#1a73e8' };
  if (ext === 'pdf') return { emoji: '📕', color: '#c5221f' };
  if (['xls','xlsx','csv'].includes(ext)) return { emoji: '📊', color: '#137333' };
  if (['doc','docx'].includes(ext)) return { emoji: '📝', color: '#1a73e8' };
  if (['zip','tar','gz','rar'].includes(ext)) return { emoji: '🗜️', color: '#e37400' };
  return { emoji: '📄', color: '#5f6368' };
}
function s3post(path, params) {
  return fetch(S3_BASE + path, { method: 'POST', body: new URLSearchParams(params) });
}

// ── helpers ───────────────────────────────────────────────────────────────────

async function s3json(url, options) {
  const res = await fetch(url, options);
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  const data = await res.json();
  if (data && (data.error === 'Not authenticated' || data.Result === 'Invalid session')) {
    redirectToLogin(); return null;
  }
  return data;
}

// Upload file via Spring backend (avoids direct browser→S3 CORS requirement)
function uploadViaBackend(file, key, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);
    form.append('key', key);
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 95));
    };
    xhr.onload = () => {
      if (xhr.status === 200) { onProgress(100); resolve(); }
      else reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.open('POST', `${S3_BASE}/upload`);
    xhr.send(form);
  });
}

// ── build folder tree from flat path list ────────────────────────────────────

function buildTree(rows) {
  const folders = new Set();
  folders.add(ROOT);
  rows.forEach(r => {
    const parts = pathSegments(r.path);
    for (let i = 1; i < parts.length; i++) {
      folders.add(parts.slice(0, i).join('/') + '/');
    }
    if (isFolder(r.path)) folders.add(r.path);
  });
  // Build nested map
  const tree = {};
  [...folders].sort().forEach(f => {
    const parts = pathSegments(f);
    let node = tree;
    parts.forEach(p => { node[p] = node[p] || {}; node = node[p]; });
  });
  return { folders: [...folders].sort(), tree };
}

// ── FolderTree component ──────────────────────────────────────────────────────

function FolderTreeNode({ path, label, depth, selectedFolder, onSelect, allFolders, T = {} }) {
  const children = allFolders.filter(f =>
    f !== path && f.startsWith(path) && pathSegments(f).length === pathSegments(path).length + 1
  );
  const [open, setOpen] = useState(depth < 2);
  const isSelected = selectedFolder === path;
  return (
    <div>
      <div
        onClick={() => { setOpen(o => !o); onSelect(path); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
          paddingLeft: 8 + depth * 14, cursor: 'pointer', borderRadius: 6,
          background: isSelected ? 'rgba(26,115,232,.2)' : 'transparent',
          color: isSelected ? '#90caf9' : (T.text || '#333'), fontWeight: isSelected ? 600 : 400,
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 14 }}>{isSelected ? '📂' : '📁'}</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        {children.length > 0 && (
          <span style={{ fontSize: 10, color: T.textMuted || '#999' }}>{open ? '▾' : '▸'}</span>
        )}
      </div>
      {open && children.map(child => (
        <FolderTreeNode key={child} path={child} label={fileName(child) || child}
          depth={depth + 1} selectedFolder={selectedFolder}
          onSelect={onSelect} allFolders={allFolders} T={T} />
      ))}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function AquaDocuments({ activeCrops = [], role = 'admin', theme = 'light' }) {
  const canWrite = role !== 'readonly';
  const canDelete = role === 'admin';
  const dk = theme === 'dark';
  const T = {
    bg:         dk ? '#1a2535' : '#fff',
    bgPanel:    dk ? '#162031' : '#fff',
    bgStripe:   dk ? '#1e2d3d' : '#f8f9fa',
    bgCard:     dk ? '#1e2d3d' : '#fff',
    border:     dk ? 'rgba(255,255,255,.1)' : '#e8eaed',
    text:       dk ? '#e2e8f0' : '#202124',
    textMuted:  dk ? '#718096' : '#888',
    textDim:    dk ? '#a0aec0' : '#5f6368',
    link:       '#90caf9',
    btnBorder:  dk ? 'rgba(255,255,255,.15)' : '#e8eaed',
  };
  const [allRows, setAllRows]         = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(ROOT);
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState('');
  const [uploading, setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [renaming, setRenaming]       = useState(null);
  const [renameVal, setRenameVal]     = useState('');
  const [viewMode, setViewMode]       = useState('grid');
  const [layout, setLayout]           = useState('gdocs');
  const [search, setSearch]           = useState('');
  const fileInputRef = useRef();

  const load = () => {
    setLoading(true);
    aquaApi.listDocs(ROOT).then(rows => { setAllRows(rows || []); }).catch(() => setAllRows([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const { folders } = buildTree(allRows);

  // Files in selected folder (direct children only)
  const folderDepth = pathSegments(selectedFolder).length;
  const inFolder = allRows.filter(r => {
    if (!r.path.startsWith(selectedFolder)) return false;
    const rel = r.path.slice(selectedFolder.length);
    if (!rel) return false;
    const relParts = rel.replace(/\/$/, '').split('/').filter(Boolean);
    return relParts.length === 1;
  });

  const displayed = search
    ? inFolder.filter(r => (r.file_name || fileName(r.path)).toLowerCase().includes(search.toLowerCase()))
    : inFolder;

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true); setErr('');
    for (const file of files) {
      const key = selectedFolder + file.name;
      setUploadStatus(`Uploading ${file.name}…`);
      try {
        await uploadViaBackend(file, key, setUploadProgress);
      } catch (ex) {
        setErr(`Upload failed: ${ex.message}`);
      }
    }
    setUploading(false); setUploadProgress(0); setUploadStatus('');
    fileInputRef.current.value = '';
    load();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    const folderPath = selectedFolder + newFolderName.trim();
    await s3post('/create-folder', { folderPath });
    setNewFolderName(''); setShowNewFolder(false);
    load();
  };

  const handleDelete = async (row) => {
    const name = row.file_name || fileName(row.path);
    if (!window.confirm(`Delete "${name}"?`)) return;
    await s3post('/delete', { key: row.path });
    load();
  };

  const handleDeleteFolder = async (folderPath) => {
    if (!window.confirm(`Delete folder "${fileName(folderPath)}" and ALL its contents?`)) return;
    await fetch(`${S3_BASE}/delete-folder?key=${encodeURIComponent(folderPath)}`, { method: 'DELETE' });
    if (selectedFolder === folderPath) setSelectedFolder(parentOf(folderPath));
    load();
  };

  const handleView = async (row) => {
    const res = await s3json(`${S3_BASE}/presigned-download?key=${encodeURIComponent(row.path)}&disposition=inline`);
    if (res && res.url) window.open(res.url, '_blank');
  };

  const handleDownload = async (row) => {
    const res = await s3json(`${S3_BASE}/presigned-download?key=${encodeURIComponent(row.path)}&disposition=attachment`);
    if (res && res.url) { const a = document.createElement('a'); a.href = res.url; a.download = fileName(row.path); a.click(); }
  };

  const handleRename = async (row) => {
    if (!renameVal.trim() || renameVal === fileName(row.path)) { setRenaming(null); return; }
    const newKey = parentOf(row.path) + renameVal.trim();
    await s3json(`${S3_BASE}/rename?oldKey=${encodeURIComponent(row.path)}&newKey=${encodeURIComponent(newKey)}`);
    setRenaming(null); load();
  };

  const quickCropFolder = async (cropCode) => {
    const folderPath = ROOT + cropCode;
    if (!folders.includes(folderPath + '/')) {
      await s3post('/create-folder', { folderPath });
      load();
    }
    setSelectedFolder(folderPath + '/');
  };

  // ── Breadcrumb ────────────────────────────────────────────────────────────

  const breadParts = pathSegments(selectedFolder);
  const breadcrumbs = breadParts.map((p, i) => ({
    label: p, path: breadParts.slice(0, i + 1).join('/') + '/'
  }));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 130px)', overflow: 'hidden' }}>

      {/* ── Left sidebar: folder tree (tree layout only) ── */}
      <div style={{
        width: layout === 'tree' ? 220 : 0,
        minWidth: layout === 'tree' ? 220 : 0,
        flexShrink: 0, background: T.bgPanel, borderRight: layout === 'tree' ? `1px solid ${T.border}` : 'none',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
        overflow: layout === 'tree' ? 'auto' : 'hidden',
        transition: 'width .2s, min-width .2s',
      }}>
        <div style={{ padding: '12px 12px 8px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, textTransform: 'uppercase', letterSpacing: '.5px' }}>
            📁 CHMR Documents
          </div>
        </div>

        {/* Crop shortcuts */}
        {activeCrops.length > 0 && (
          <div style={{ padding: '8px 12px', borderBottom: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginBottom: 6 }}>QUICK — BY CROP</div>
            {activeCrops.map(c => (
              <div key={c.id} onClick={() => quickCropFolder(c.crop_code)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
                  cursor: 'pointer', borderRadius: 6, fontSize: 12,
                  background: selectedFolder === ROOT + c.crop_code + '/' ? 'rgba(26,115,232,.2)' : 'transparent',
                  color: selectedFolder === ROOT + c.crop_code + '/' ? '#90caf9' : T.text }}>
                <span style={{ fontSize: 12 }}>🌱</span>
                {c.crop_code}
              </div>
            ))}
          </div>
        )}

        {/* Full tree */}
        <div style={{ flex: 1, padding: '8px 4px', overflowY: 'auto' }}>
          {loading ? <div style={{ padding: 12, color: T.textMuted, fontSize: 12 }}>Loading…</div> : (
            <FolderTreeNode
              path={ROOT} label="CHMR Root" depth={0}
              selectedFolder={selectedFolder}
              onSelect={setSelectedFolder}
              allFolders={folders}
              T={T}
            />
          )}
        </div>
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: T.bg }}>

        {/* Toolbar */}
        <div style={{ padding: '8px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          {canWrite && (
            <>
              <button className="btn btn-primary" style={{ padding: '5px 14px', fontSize: 13 }}
                onClick={() => fileInputRef.current.click()} disabled={uploading}>
                ⬆️ Upload
              </button>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleUpload} />
            </>
          )}

          {canWrite && (
            <button className="btn btn-secondary" style={{ padding: '5px 14px', fontSize: 13 }}
              onClick={() => setShowNewFolder(s => !s)}>
              🗂️ New Folder
            </button>
          )}

          <div style={{ flex: 1 }} />

          <input className="form-control" style={{ width: 200, padding: '4px 10px', fontSize: 13 }}
            placeholder="Search files…" value={search} onChange={e => setSearch(e.target.value)} />

          <button onClick={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')}
            title={viewMode === 'grid' ? 'Switch to list view' : 'Switch to grid view'}
            style={{ background: 'none', border: `1px solid ${T.btnBorder}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 14, color: T.textDim }}>
            {viewMode === 'grid' ? '☰' : '⊞'}
          </button>

          <button onClick={() => setLayout(l => l === 'gdocs' ? 'tree' : 'gdocs')}
            title={layout === 'gdocs' ? 'Show folder tree' : 'Hide folder tree'}
            style={{ background: layout === 'tree' ? (dk ? 'rgba(26,115,232,.25)' : '#e8f0fe') : 'none', border: `1px solid ${T.btnBorder}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 14, color: layout === 'tree' ? '#90caf9' : T.textDim }}>
            🌳
          </button>

          <button onClick={load}
            style={{ background: 'none', border: `1px solid ${T.btnBorder}`, borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 14, color: T.textDim }}>
            🔄
          </button>
        </div>

        {/* New folder bar */}
        {showNewFolder && (
          <div style={{ padding: '8px 16px', background: T.bgStripe, borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 16, color: '#fbbc04' }}>📁</span>
            <input className="form-control" style={{ width: 240, padding: '4px 10px', fontSize: 13 }}
              placeholder="Folder name" autoFocus value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setShowNewFolder(false); }} />
            <button className="btn btn-primary" style={{ padding: '4px 14px', fontSize: 13 }} onClick={handleCreateFolder}>Create</button>
            <button className="btn btn-secondary" style={{ padding: '4px 12px', fontSize: 13 }} onClick={() => setShowNewFolder(false)}>Cancel</button>
            <span style={{ fontSize: 12, color: T.textMuted }}>Inside: {selectedFolder}</span>
          </div>
        )}

        {/* Upload progress bar */}
        {uploading && (
          <div style={{ padding: '8px 16px', background: dk ? 'rgba(26,115,232,.15)' : '#e8f0fe', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#90caf9', marginBottom: 4 }}>
              <span>{uploadStatus}</span><span>{uploadProgress}%</span>
            </div>
            <div style={{ height: 6, background: dk ? 'rgba(26,115,232,.3)' : '#c5d8fb', borderRadius: 3 }}>
              <div style={{ height: '100%', background: '#1a73e8', borderRadius: 3, width: `${uploadProgress}%`, transition: 'width .2s' }} />
            </div>
          </div>
        )}

        {/* Error banner */}
        {err && (
          <div style={{ padding: '8px 16px', background: '#fce8e6', color: '#c5221f', fontSize: 13, borderBottom: '1px solid #f5c6c4', flexShrink: 0 }}>
            <span style={{ marginRight: 6 }}>⚠️</span>{err}
            <button onClick={() => setErr('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#c5221f', fontWeight: 700 }}>×</button>
          </div>
        )}

        {/* Breadcrumb */}
        <div style={{ padding: '6px 16px', background: T.bgStripe, borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 13 }}>
          <span onClick={() => setSelectedFolder(ROOT)} style={{ cursor: 'pointer', color: '#90caf9' }}>CHMR</span>
          {breadcrumbs.slice(1).map((b, i) => (
            <span key={i}>
              <span style={{ color: T.textMuted, margin: '0 4px' }}>/</span>
              <span onClick={() => setSelectedFolder(b.path)} style={{ cursor: 'pointer', color: '#90caf9' }}>{b.label}</span>
            </span>
          ))}
        </div>

        {/* File area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {loading ? (
            <div className="loading-msg">Loading documents…</div>
          ) : displayed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: T.textMuted }}>
              <span style={{ fontSize: 48, marginBottom: 12, display: 'block' }}>📂</span>
              <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>{search ? 'No matching files' : 'This folder is empty'}</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Click <strong>Upload</strong> to add files or <strong>New Folder</strong> to create subfolders</div>
            </div>
          ) : viewMode === 'grid' ? (
            <GridView rows={displayed} folderDepth={folderDepth}
              onView={handleView} onDownload={handleDownload}
              onDelete={handleDelete} onRename={(r) => { setRenaming(r.path); setRenameVal(r.file_name || fileName(r.path)); }}
              onOpenFolder={setSelectedFolder}
              renaming={renaming} renameVal={renameVal} setRenameVal={setRenameVal}
              onRenameSubmit={handleRename} onDeleteFolder={handleDeleteFolder}
              canWrite={canWrite} canDelete={canDelete} T={T} />
          ) : (
            <ListView rows={displayed}
              onView={handleView} onDownload={handleDownload}
              onDelete={handleDelete} onRename={(r) => { setRenaming(r.path); setRenameVal(r.file_name || fileName(r.path)); }}
              onOpenFolder={setSelectedFolder}
              renaming={renaming} renameVal={renameVal} setRenameVal={setRenameVal}
              onRenameSubmit={handleRename} onDeleteFolder={handleDeleteFolder}
              canWrite={canWrite} canDelete={canDelete} T={T} />
          )}
        </div>

        {/* Footer count */}
        <div style={{ padding: '6px 16px', borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.textMuted, flexShrink: 0 }}>
          {displayed.length} item{displayed.length !== 1 ? 's' : ''} in {selectedFolder}
        </div>
      </div>
    </div>
  );
}

// ── Grid view ──────────────────────────────────────────────────────────────────

function GridView({ rows, onView, onDownload, onDelete, onRename, onOpenFolder, renaming, renameVal, setRenameVal, onRenameSubmit, onDeleteFolder, canWrite, canDelete, T = {} }) {
  const shadowBase = T.bg === '#1a2535' ? '0 1px 3px rgba(0,0,0,.3)' : '0 1px 3px rgba(0,0,0,.06)';
  const shadowHover = T.bg === '#1a2535' ? '0 3px 12px rgba(0,0,0,.5)' : '0 3px 10px rgba(0,0,0,.12)';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
      {rows.map(r => {
        const name = r.file_name || fileName(r.path);
        const folder = isFolder(r.path);
        const { emoji, color } = folder ? { emoji: '📁', color: '#fbbc04' } : fileIcon(name);
        return (
          <div key={r.path} style={{ border: `1px solid ${T.border || '#e8eaed'}`, borderRadius: 10, padding: 12, background: T.bgCard || '#fff',
            cursor: folder ? 'pointer' : 'default',
            boxShadow: shadowBase, transition: 'box-shadow .15s' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = shadowHover}
            onMouseLeave={e => e.currentTarget.style.boxShadow = shadowBase}
            onDoubleClick={() => folder ? onOpenFolder(r.path) : onView(r)}>
            {/* Icon */}
            <div style={{ textAlign: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 42 }}>{emoji}</span>
            </div>
            {/* Name */}
            {renaming === r.path ? (
              <input autoFocus style={{ width: '100%', fontSize: 12, border: '1px solid #1a73e8', borderRadius: 4, padding: '2px 4px', textAlign: 'center', background: T.bgStripe || '#fff', color: T.text || '#202124' }}
                value={renameVal} onChange={e => setRenameVal(e.target.value)}
                onBlur={() => onRenameSubmit(r)}
                onKeyDown={e => { if (e.key === 'Enter') onRenameSubmit(r); if (e.key === 'Escape') onRenameSubmit({ ...r, _cancel: true }); }} />
            ) : (
              <div style={{ fontSize: 12, textAlign: 'center', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: T.text || '#202124' }}
                title={name}>{name}</div>
            )}
            {r.file_size && <div style={{ fontSize: 10, color: T.textMuted || '#888', textAlign: 'center', marginTop: 2 }}>{fmtSize(r.file_size)}</div>}
            {r.created_at && <div style={{ fontSize: 10, color: T.textMuted || '#aaa', textAlign: 'center' }}>{fmtDate(r.created_at)}</div>}
            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 8 }}>
              {!folder && <>
                <button title="View" onClick={() => onView(r)} style={iconBtn('#1a73e8')}>👁</button>
                <button title="Download" onClick={() => onDownload(r)} style={iconBtn('#137333')}>⬇️</button>
              </>}
              {folder && (
                <button title="Open" onClick={() => onOpenFolder(r.path)} style={iconBtn('#fbbc04')}>📂</button>
              )}
              {canWrite && <button title="Rename" onClick={() => onRename(r)} style={iconBtn('#5f6368')}>✏️</button>}
              {canDelete && <button title="Delete" onClick={() => folder ? onDeleteFolder(r.path) : onDelete(r)} style={iconBtn('#c5221f')}>🗑️</button>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── List view ──────────────────────────────────────────────────────────────────

function ListView({ rows, onView, onDownload, onDelete, onRename, onOpenFolder, renaming, renameVal, setRenameVal, onRenameSubmit, onDeleteFolder, canWrite, canDelete, T = {} }) {
  return (
    <table className="data-table" style={{ width: '100%' }}>
      <thead>
        <tr>
          <th>Name</th>
          <th style={{ textAlign: 'right', width: 80 }}>Size</th>
          <th style={{ width: 140 }}>Uploaded</th>
          <th style={{ width: 130 }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => {
          const name = r.file_name || fileName(r.path);
          const folder = isFolder(r.path);
          const { emoji } = folder ? { emoji: '📁' } : fileIcon(name);
          return (
            <tr key={r.path} style={{ cursor: folder ? 'pointer' : 'default' }}
              onDoubleClick={() => folder && onOpenFolder(r.path)}>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18, flexShrink: 0 }}>{emoji}</span>
                  {renaming === r.path ? (
                    <input autoFocus style={{ fontSize: 13, border: '1px solid #1a73e8', borderRadius: 4, padding: '2px 6px', width: 240, background: T.bgStripe || '#fff', color: T.text || '#202124' }}
                      value={renameVal} onChange={e => setRenameVal(e.target.value)}
                      onBlur={() => onRenameSubmit(r)}
                      onKeyDown={e => { if (e.key === 'Enter') onRenameSubmit(r); if (e.key === 'Escape') onRenameSubmit({ ...r }); }} />
                  ) : (
                    <span style={{ fontWeight: 500, fontSize: 13 }}>{name}</span>
                  )}
                </div>
              </td>
              <td style={{ textAlign: 'right', color: T.textMuted || '#888', fontSize: 12 }}>{fmtSize(r.file_size)}</td>
              <td style={{ color: T.textMuted || '#888', fontSize: 12 }}>{fmtDate(r.created_at)}</td>
              <td>
                <div style={{ display: 'flex', gap: 4 }}>
                  {!folder && <>
                    <button title="View" onClick={() => onView(r)} style={iconBtn('#1a73e8')}>👁</button>
                    <button title="Download" onClick={() => onDownload(r)} style={iconBtn('#137333')}>⬇️</button>
                  </>}
                  {folder && (
                    <button title="Open" onClick={() => onOpenFolder(r.path)} style={iconBtn('#fbbc04')}>📂</button>
                  )}
                  {canWrite && <button title="Rename" onClick={() => onRename(r)} style={iconBtn('#5f6368')}>✏️</button>}
                  {canDelete && <button title="Delete" onClick={() => folder ? onDeleteFolder(r.path) : onDelete(r)} style={iconBtn('#c5221f')}>🗑️</button>}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function iconBtn(color) {
  return {
    background: 'none', border: 'none', cursor: 'pointer',
    color, fontSize: 13, padding: '3px 5px', borderRadius: 4,
  };
}
